"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@lib/utils/utils";
import { createClient } from "@/backend/lib/supabase/client";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { Link } from "@/i18n/navigation";
import { isValidRedirect } from "@/frontend/lib/utils/redirect";

async function waitForSessionReady(supabase: ReturnType<typeof createClient>) {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.user) {
      return;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
}

export function LoginForm({
  className,
  onSwitchToSignup,
  onSwitchToForgot,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  onClose: () => void;
  onSwitchToSignup: () => void;
  onSwitchToForgot: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const supabase = createClient();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      await waitForSessionReady(supabase);

      // Redirect to original URL if provided, otherwise to home
      const redirect = searchParams.get("redirect");
      const safeRedirect = redirect && isValidRedirect(redirect) ? redirect : "/";
      router.push(safeRedirect);
      setIsLoading(false);
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : t("auth.login.error"));
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.login.title")}</CardTitle>
          <CardDescription>{t("auth.login.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">{t("auth.login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.common.emailPlaceholder")}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">{t("auth.login.password")}</Label>
                  <Link
                    href="#"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      onSwitchToForgot();
                    }}
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? t("auth.login.loggingIn") : t("auth.login.loginButton")}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              {t("auth.login.noAccount")}{" "}
              <Button type="button" variant="link" className="h-auto p-0" onClick={onSwitchToSignup}>
                {t("auth.login.signUp")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
